import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { StrategyRdCoachThreadSummary } from "@/types/strategyRdCoach";
import { cn } from "@/lib/utils";

type Props = {
  threads: StrategyRdCoachThreadSummary[];
  activeId: string | null;
  busy?: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
};

export function StrategyRdCoachThreads({
  threads,
  activeId,
  busy,
  onSelect,
  onNew,
  onDelete,
}: Props) {
  return (
    <div className="flex flex-col gap-3">
      {/* The app's outline-brand button: rectangular, primary font, sentence case. */}
      <Button
        type="button"
        variant="outline-brand"
        size="sm"
        className="h-8 w-full justify-start gap-1.5 rounded-[6px] !px-3 font-[family-name:var(--font-primary)] text-xs normal-case tracking-normal shadow-none"
        disabled={busy}
        onClick={onNew}
      >
        <Plus className="size-3.5" aria-hidden />
        New chat
      </Button>
      {threads.length === 0 ? (
        <p className="!m-0 !px-1 font-[family-name:var(--font-primary)] text-xs text-gray-400 dark:text-muted-foreground">
          No saved chats yet.
        </p>
      ) : (
        <ul className="!m-0 flex !list-none flex-col gap-0.5 !p-0">
          {threads.map((t) => (
            <li key={t.id} className="group flex items-center gap-0.5">
              <button
                type="button"
                disabled={busy}
                onClick={() => onSelect(t.id)}
                className={cn(
                  "min-w-0 flex-1 truncate !rounded-[6px] !px-2.5 !py-2 text-left font-[family-name:var(--font-primary)] !text-[13px] transition-colors",
                  t.id === activeId
                    ? "!bg-[color:var(--brand-tropical-surf)]/10 !text-[color:var(--brand-tropical-surf)]"
                    : "!text-gray-500 hover:!bg-muted/60 hover:!text-gray-600 dark:!text-muted-foreground dark:hover:!text-foreground"
                )}
              >
                {t.title}
              </button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="size-7 shrink-0 rounded-[6px] !p-0 text-gray-400 opacity-0 transition hover:text-[#e00101] focus-visible:opacity-100 group-hover:opacity-100"
                disabled={busy}
                aria-label={`Delete ${t.title}`}
                onClick={() => onDelete(t.id)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
